import {
    type CSSProperties,
    useEffect,
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type InputOTPType,
    InputOTP
} from "ncore-ui-kit";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";

const FIT_STYLE: CSSProperties = {
    maxWidth: "100%"
};

type VerifyStatus = "idle" | "verifying" | "success" | "error";

const NAME = "InputOTP";

const USAGE_CODE = `const [code, setCode] = useState("");
const [status, setStatus] = useState("idle");

const handleChange = (nextCode: string) => {
    setCode(nextCode);
    setStatus("idle");
};

const handleComplete = async (nextCode: string) => {
    setStatus("verifying");

    const isCorrect = await verifyCode(nextCode);

    setStatus(isCorrect ? "success" : "error");
};

<InputOTP
    type={status === "error" ? "danger" : "primary"}
    hintText={status === "error" ? "Wrong code." : undefined}
    isLoading={status === "verifying"}
    onComplete={handleComplete}
    title="Verification code"
    onChange={handleChange}
    groupSize={3}
    value={code}
    length={6}
/>`;

const STATUS_TYPES: Record<VerifyStatus, InputOTPType> = {
    verifying: "primary",
    success: "success",
    error: "danger",
    idle: "primary"
};

const TYPES = [
    "success",
    "warning",
    "danger",
    "info"
] as const;

const SIZES = [
    "medium",
    "large"
] as const;

const CORRECT_CODE = "482915";

const VERIFY_DELAY = 1200;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("inputOTPPage.lengthTitle"),
        id: "length"
    },
    {
        title: t("inputOTPPage.charactersTitle"),
        id: "characters"
    },
    {
        title: t("inputOTPPage.statusTitle"),
        id: "status"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("section.states"),
        id: "states"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const InputOTPPage = () => {
    const {
        t
    } = useDocsI18n();

    const [
        code,
        setCode
    ] = useState("");

    const [
        status,
        setStatus
    ] = useState<VerifyStatus>("idle");

    useEffect(() => {
        if(status !== "verifying") {
            return;
        }

        const timer = window.setTimeout(() => setStatus(code === CORRECT_CODE ? "success" : "error"), VERIFY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        status,
        code
    ]);

    const hints: Record<VerifyStatus, string | undefined> = {
        verifying: t("inputOTPPage.hintVerifying"),
        success: t("inputOTPPage.hintSuccess"),
        error: t("inputOTPPage.hintError"),
        idle: undefined
    };

    const typeHints: Record<(typeof TYPES)[number], string> = {
        success: t("inputOTPPage.hintSuccess"),
        warning: t("inputOTPPage.hintWarning"),
        danger: t("inputOTPPage.hintError"),
        info: t("inputOTPPage.hintInfo")
    };

    const handleChange = (nextCode: string) => {
        setCode(nextCode);

        setStatus("idle");
    };

    const handleComplete = () => {
        setStatus("verifying");
    };

    return <Article>
        <ComponentHeader
            lead={t("inputOTPPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("inputOTPPage.overviewText")}
            </Paragraph>
            <Paragraph>
                {t("inputOTPPage.overviewAlternatives")}
            </Paragraph>
            <Preview
                caption={t("inputOTPPage.overviewCaption")}
            >
                <InputOTP
                    title={t("inputOTPPage.codeTitle")}
                    isLoading={status === "verifying"}
                    onComplete={handleComplete}
                    type={STATUS_TYPES[status]}
                    hintText={hints[status]}
                    onChange={handleChange}
                    groupSize={3}
                    value={code}
                    length={6}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("inputOTPPage.lengthTitle")}
            id="length"
        >
            <Paragraph>
                {t("inputOTPPage.lengthText")}
            </Paragraph>
            <Preview>
                <InputOTP
                    initialValue="482915"
                    title="groupSize 3"
                    groupSize={3}
                    length={6}
                />
                <InputOTP
                    initialValue="7204"
                    title="length 4"
                    length={4}
                />
                <InputOTP
                    initialValue="482915"
                    title="length 6"
                    length={6}
                />
            </Preview>
        </Section>
        <Section
            title={t("inputOTPPage.charactersTitle")}
            id="characters"
        >
            <Paragraph>
                {t("inputOTPPage.charactersText")}
            </Paragraph>
            <Preview>
                <InputOTP
                    characterSet="alphanumeric"
                    title="alphanumeric"
                    initialValue="A7K2"
                    length={4}
                />
                <InputOTP
                    initialValue="72"
                    title="isMasked"
                    isMasked={true}
                    length={4}
                />
            </Preview>
        </Section>
        <Section
            title={t("inputOTPPage.statusTitle")}
            id="status"
        >
            <Paragraph>
                {t("inputOTPPage.statusText")}
            </Paragraph>
            <Preview>
                {
                    TYPES.map((type) => <InputOTP
                        hintText={typeHints[type]}
                        initialValue="482915"
                        groupSize={3}
                        title={type}
                        type={type}
                        key={type}
                    />)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("inputOTPPage.sizesText")}
            </Paragraph>
            <Preview>
                {
                    SIZES.map((size) => <div
                        style={FIT_STYLE}
                        key={size}
                    >
                        <InputOTP
                            initialValue="482915"
                            groupSize={3}
                            title={size}
                            size={size}
                        />
                    </div>)
                }
            </Preview>
        </Section>
        <Section
            title={t("section.states")}
            id="states"
        >
            <Paragraph>
                {t("inputOTPPage.statesText")}
            </Paragraph>
            <Preview>
                <div
                    style={FIT_STYLE}
                >
                    <InputOTP
                        title={t("inputOTPPage.disabled")}
                        initialValue="482"
                        isDisabled={true}
                        groupSize={3}
                    />
                </div>
                <div
                    style={FIT_STYLE}
                >
                    <InputOTP
                        hintText={t("inputOTPPage.hintVerifying")}
                        title={t("inputOTPPage.loading")}
                        initialValue="482915"
                        isLoading={true}
                        groupSize={3}
                    />
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("inputOTPPage.a11yInput"),
                    t("inputOTPPage.a11yHint"),
                    t("inputOTPPage.a11yStates"),
                    t("inputOTPPage.a11yAutofill"),
                    t("inputOTPPage.a11yPointer"),
                    t("inputOTPPage.a11yPaste"),
                    t("inputOTPPage.a11yDirection")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("inputOTPPage.propValue"),
                        type: "string",
                        name: "value"
                    },
                    {
                        description: t("inputOTPPage.propInitialValue"),
                        name: "initialValue",
                        type: "string"
                    },
                    {
                        description: t("inputOTPPage.propOnChange"),
                        type: "(code: string) => void",
                        name: "onChange"
                    },
                    {
                        description: t("inputOTPPage.propOnComplete"),
                        type: "(code: string) => void",
                        name: "onComplete"
                    },
                    {
                        description: t("inputOTPPage.propLength"),
                        defaultValue: "6",
                        name: "length",
                        type: "4 | 6"
                    },
                    {
                        description: t("inputOTPPage.propGroupSize"),
                        name: "groupSize",
                        type: "number"
                    },
                    {
                        description: t("inputOTPPage.propCharacterSet"),
                        type: "\"numeric\" | \"alphanumeric\"",
                        defaultValue: "\"numeric\"",
                        name: "characterSet"
                    },
                    {
                        description: t("inputOTPPage.propIsMasked"),
                        defaultValue: "false",
                        name: "isMasked",
                        type: "boolean"
                    },
                    {
                        type: "\"primary\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("inputOTPPage.propType"),
                        defaultValue: "\"primary\"",
                        name: "type"
                    },
                    {
                        description: t("inputOTPPage.propSize"),
                        type: "\"medium\" | \"large\"",
                        name: "size"
                    },
                    {
                        description: t("inputOTPPage.propTitle"),
                        type: "string",
                        name: "title"
                    },
                    {
                        description: t("inputOTPPage.propHintText"),
                        name: "hintText",
                        type: "string"
                    },
                    {
                        description: t("inputOTPPage.propIsDisabled"),
                        defaultValue: "false",
                        name: "isDisabled",
                        type: "boolean"
                    },
                    {
                        description: t("inputOTPPage.propIsLoading"),
                        defaultValue: "false",
                        name: "isLoading",
                        type: "boolean"
                    },
                    {
                        description: t("inputOTPPage.propId"),
                        type: "string",
                        name: "id"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("inputOTPPage.propCustomTheme"),
                        name: "customTheme"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("inputOTPPage.propsNote")}
            </Paragraph>
            <Paragraph
                isMuted={true}
            >
                {t("inputOTPPage.refNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default InputOTPPage;
